import {
  NOYAU_MODE_LABEL,
  PAGE_EDITOR_IDS,
} from '@/components/admin/experiences/page-editor/contracts';
import { Segmented } from '@/components/admin/experiences/page-editor/segmented';
import { ReferenceControl } from '@/components/admin/versioned/reference-control';
import { buttonVariants } from '@/components/ui/button';
import { Callout } from '@/components/ui/callout';
import {
  type NoyauPreviewPair,
  movedPair,
  noyauName,
  noyauTextsStateLine,
} from '@/lib/noyau-preview';
import { cn } from '@/lib/utils';
import { PILL_INK_CLASS, PILL_SHAPE_CLASS } from '@/lib/variable-pill';
import type {
  ExperienceBlockDraft,
  NoyauBlockMode,
  NoyauCode,
  NoyauTextsDetail,
  VersionPin,
} from '@matter_extractor/shared';
import {
  NOYAUX,
  NOYAU_MAX_GAP_MAX,
  NOYAU_MAX_GAP_MIN,
  NOYAU_TEXTS_ID,
  noyauOf,
  noyauPoints,
  oppositeNoyau,
} from '@matter_extractor/shared';
import { Link } from '@tanstack/react-router';
import { ExternalLink } from 'lucide-react';
import { useId } from 'react';

/**
 * L'inspecteur du bloc Noyau (noyau.md §3.2, §3.5, N13, N20 ; maquette
 * `EditeurPageNoyau` ; issue #151) : le MODE, la référence aux TEXTES des noyaux
 * et sa version, « APERÇU AVEC » - et la règle qui fait du choix la seule sortie
 * de sa page.
 *
 * ## Le seuil d'écart, en premier choix seulement (noyau.md §3.8, N68 ; issue #169)
 *
 * Under the mode, and for the first choice alone (planche `EcartInspecteur`):
 * the number field of « Durée automatique », clamped to the integers of 0 to
 * 100 while one types - out of those bounds the content is one the schema
 * refuses, and the refusal would be a 409 nothing on screen explains - and a
 * sentence that says the rule AT the threshold set, with its own words at the
 * two bounds. Every Noyau block carries the field, and a block moved to
 * another mode keeps it without effect: the section is hidden, never the value
 * reset.
 *
 * ## La version est celle de l'EXPÉRIENCE
 *
 * The reference is one for every Noyau block of the experience (§3.5), so the
 * pill-menu writes the draft's `noyauTexts` and not the block: set it here, and
 * every block of the timeline reads the same version - `latest` by default, a
 * precise version when pinned (vitrine.md §3.1, planche `references.html`). The
 * line under it says what each pointer of the texts holds, off the singleton's
 * detail, and the door leads to Réglages > Noyaux, where they are typed - never
 * here (§6.4).
 *
 * ## « Aperçu avec » is the PREVIEW's, and it is not stored
 *
 * The block carries its mode and its threshold, and nothing else. The pair of example is posture
 * of the editor - the régime of « L'aperçu ne garde rien » - held above the
 * body so the full-screen preview opens on it. A first choice shows its two
 * noyaux; a second choice and a portrait show the RETAINED one, which the
 * simulation reads as the principal of the pair - the person took the
 * principal - and a second choice names the mirror it will face (§2.2).
 */

const LABEL = 'block font-semibold text-gray-800 text-xs';
const HINT = 'text-gray-600 text-xs leading-4';
const SELECT =
  'h-9 w-full cursor-pointer rounded-md border-2 border-gray-300 bg-white pr-3 pl-7 text-sm focus-visible:border-bleu-nuit focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-60';
/**
 * The field of « Durée automatique » (`page-panel`), the number field of this
 * editor - at the 96 px of the planche `EcartInspecteur` and without its
 * `w-full`, which Tailwind orders after `w-24`: beside it, the field took the
 * whole row and folded « points au plus, de 0 à 100 » in two.
 */
const FIELD =
  'h-9 w-24 shrink-0 rounded-md border-2 border-gray-300 bg-white px-3 text-sm read-only:bg-gray-50 read-only:text-gray-700 focus-visible:border-bleu-nuit focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1';

/** What each mode shows, under the toggle (maquette `EditeurPageNoyau`, and `EcartInspecteur` since #169). */
const MODE_HINT: Readonly<Record<NoyauBlockMode, string>> = {
  'first-choice':
    'Le premier texte, dans le noyau principal et le noyau second. Selon l’écart de leurs notes, la carte prise donne le noyau retenu, ou le principal l’emporte.',
  'second-choice':
    'Le second texte, dans le noyau retenu et son opposé. La carte prise est enregistrée, et ne change rien.',
  portrait: 'Le portrait du noyau retenu, en texte riche.',
};

const MODES: readonly { value: NoyauBlockMode; label: string }[] = [
  { value: 'first-choice', label: NOYAU_MODE_LABEL['first-choice'] },
  { value: 'second-choice', label: NOYAU_MODE_LABEL['second-choice'] },
  { value: 'portrait', label: NOYAU_MODE_LABEL.portrait },
];

export interface NoyauPanelProps {
  readonly block: Extract<ExperienceBlockDraft, { type: 'noyau' }>;
  readonly readOnly: boolean;
  /** What the experience reads the texts at (§3.5): `latest` by default, or a pinned version. */
  readonly pin: VersionPin;
  /** How many Noyau blocks the experience carries - the reference is worth for all of them. */
  readonly blockCount: number;
  /** The singleton's detail; null with no version at all; undefined while it is read. */
  readonly detail: NoyauTextsDetail | null | undefined;
  readonly pair: NoyauPreviewPair;
  readonly onChange: (next: ExperienceBlockDraft) => void;
  readonly onPin: (pin: VersionPin) => void;
  readonly onPair: (pair: NoyauPreviewPair) => void;
}

/**
 * The rule at the threshold set (planche `EcartInspecteur`): its own sentence
 * at each bound - at 0 only a tie leaves the choice to the person, at 100 the
 * card always decides - and the general one between, where « 1 point » is
 * written in the singular.
 */
function maxGapHint(maxGap: number): string {
  if (maxGap <= NOYAU_MAX_GAP_MIN) {
    return 'La personne ne tranche que sur une égalité du principal et du second. Sinon, le principal est retenu, et la carte est enregistrée sans rien changer.';
  }
  if (maxGap >= NOYAU_MAX_GAP_MAX) {
    return 'La personne tranche toujours : la carte prise donne le noyau retenu, quel que soit l’écart.';
  }
  return `La carte prise donne le noyau retenu si le principal et le second sont à ${noyauPoints(maxGap)} au plus l’un de l’autre. Au-delà, le principal est retenu, et la carte est enregistrée sans rien changer.`;
}

/** What is typed, as a threshold the schema accepts: an integer of 0 to 100, emptied reading 0. */
function clampedMaxGap(typed: number): number {
  return Math.min(Math.max(Math.round(typed), NOYAU_MAX_GAP_MIN), NOYAU_MAX_GAP_MAX);
}

/** The line under « Aperçu avec », per mode. */
const PREVIEW_HINT: Readonly<Record<NoyauBlockMode, string>> = {
  'first-choice':
    'L’aperçu montre les vrais textes de cette paire. En session, la paire vient de la détermination.',
  'second-choice':
    'L’aperçu montre les vrais textes de cette paire. En session, le noyau retenu vient du premier choix.',
  portrait:
    'L’aperçu montre le vrai portrait de ce noyau. En session, le noyau retenu vient du premier choix.',
};

export function NoyauPanel({
  block,
  readOnly,
  pin,
  blockCount,
  detail,
  pair,
  onChange,
  onPin,
  onPair,
}: NoyauPanelProps) {
  const principalId = useId();
  const secondId = useId();
  const maxGapId = useId();
  const maxGapUnitId = useId();
  const maxGapHintId = useId();
  const choice = block.mode !== 'portrait';

  return (
    <div data-testid={PAGE_EDITOR_IDS.noyauInspector} className="space-y-4">
      <div className="space-y-1.5">
        <p className={LABEL}>Mode</p>
        <Segmented
          label="Mode du bloc Noyau"
          size="sm"
          testId={PAGE_EDITOR_IDS.noyauMode}
          value={block.mode}
          options={MODES}
          readOnly={readOnly}
          onChange={(mode) => onChange({ ...block, mode })}
        />
        <p className={HINT}>{MODE_HINT[block.mode]}</p>
      </div>

      {block.mode === 'first-choice' ? (
        <div className="space-y-1.5">
          <label htmlFor={maxGapId} className={LABEL}>
            Seuil d’écart
          </label>
          <div className="flex items-center gap-2">
            <input
              id={maxGapId}
              type="number"
              inputMode="numeric"
              data-testid={PAGE_EDITOR_IDS.noyauMaxGap}
              value={block.maxGap}
              readOnly={readOnly}
              min={NOYAU_MAX_GAP_MIN}
              max={NOYAU_MAX_GAP_MAX}
              step={1}
              aria-describedby={`${maxGapUnitId} ${maxGapHintId}`}
              onChange={(event) => {
                const typed = Number(event.currentTarget.value.trim());
                if (Number.isNaN(typed)) return;
                onChange({ ...block, maxGap: clampedMaxGap(typed) });
              }}
              className={FIELD}
            />
            <span id={maxGapUnitId} className="text-gray-600 text-xs">
              points au plus, de {NOYAU_MAX_GAP_MIN} à {NOYAU_MAX_GAP_MAX}
            </span>
          </div>
          <p id={maxGapHintId} data-testid={PAGE_EDITOR_IDS.noyauMaxGapHint} className={HINT}>
            {maxGapHint(block.maxGap)}
          </p>
        </div>
      ) : null}

      <div className="space-y-1.5">
        <p className={LABEL}>Textes des noyaux</p>
        <ReferenceControl
          subject={{
            kind: 'noyau-texts',
            id: NOYAU_TEXTS_ID,
            name: 'Les textes des noyaux',
            who: 'cette expérience',
            pin,
            developmentNumber: detail?.development.number ?? null,
          }}
          readOnly={readOnly}
          testId={PAGE_EDITOR_IDS.noyauTextsPin}
          onPin={onPin}
        />
        <p className={HINT}>
          {blockCount > 1
            ? `Vaut pour les ${blockCount} blocs Noyau de l’expérience.`
            : 'Vaut pour tous les blocs Noyau de l’expérience.'}
        </p>
        <p
          data-testid={PAGE_EDITOR_IDS.noyauTextsState}
          className="text-gray-800 text-xs leading-4"
        >
          {detail === undefined ? 'Lecture des textes…' : noyauTextsStateLine(detail)}
        </p>
        <Link
          to="/admin/reglages/noyaux"
          data-testid={PAGE_EDITOR_IDS.noyauTextsOpen}
          className={cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'no-underline')}
        >
          <ExternalLink aria-hidden className="size-4" />
          Ouvrir Réglages &gt; Noyaux
        </Link>
      </div>

      <fieldset className="m-0 min-w-0 space-y-1.5 border-0 p-0">
        <legend className={LABEL}>Aperçu avec</legend>
        <div className="grid grid-cols-[4.5rem_minmax(0,1fr)] items-center gap-x-2 gap-y-2">
          <label htmlFor={principalId} className="text-gray-700 text-xs">
            {block.mode === 'first-choice' ? 'Principal' : 'Retenu'}
          </label>
          <NoyauSelect
            id={principalId}
            testId={PAGE_EDITOR_IDS.noyauPreviewPrincipal}
            value={pair.principal}
            onChange={(code) => onPair(movedPair(pair, 'principal', code))}
          />
          {block.mode === 'first-choice' ? (
            <>
              <label htmlFor={secondId} className="text-gray-700 text-xs">
                Second
              </label>
              <NoyauSelect
                id={secondId}
                testId={PAGE_EDITOR_IDS.noyauPreviewSecond}
                value={pair.second}
                onChange={(code) => onPair(movedPair(pair, 'second', code))}
              />
            </>
          ) : null}
        </div>
        {block.mode === 'second-choice' ? (
          <p className={HINT}>Face à son opposé, {noyauName(oppositeNoyau(pair.principal))}.</p>
        ) : null}
        <p className={HINT}>{PREVIEW_HINT[block.mode]}</p>
      </fieldset>

      {choice ? (
        <Callout tone="neutral" data-testid={PAGE_EDITOR_IDS.noyauExitNote}>
          Le choix est la seule sortie de sa page : aucun Bouton ne change de page à côté de lui, et
          la page n’a pas de durée automatique.
        </Callout>
      ) : null}

      {block.mode === 'first-choice' ? (
        <p className={HINT}>
          Insérable à partir de la page suivante par{' '}
          <span className={cn(PILL_SHAPE_CLASS, PILL_INK_CLASS.noyau)}>@noyau</span>
        </p>
      ) : null}
    </div>
  );
}

/** One select of « Aperçu avec »: the six noyaux of the table, the colour dot of the one chosen. */
function NoyauSelect({
  id,
  testId,
  value,
  onChange,
}: {
  readonly id: string;
  readonly testId: string;
  readonly value: NoyauCode;
  readonly onChange: (code: NoyauCode) => void;
}) {
  return (
    <div className="relative min-w-0">
      {/* The operator sees the colours of the noyaux, the person never does (N19). */}
      <span
        aria-hidden
        className="-translate-y-1/2 pointer-events-none absolute top-1/2 left-2.5 size-2 rounded-pill"
        style={{ backgroundColor: noyauOf(value).color }}
      />
      <select
        id={id}
        data-testid={testId}
        value={value}
        onChange={(event) => {
          const next = NOYAUX.find((noyau) => noyau.code === event.currentTarget.value);
          if (next !== undefined) onChange(next.code);
        }}
        className={SELECT}
      >
        {NOYAUX.map((noyau) => (
          <option key={noyau.code} value={noyau.code}>
            {noyau.name}
          </option>
        ))}
      </select>
    </div>
  );
}
